import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Card, Field, Input, TabNotPorted } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductLink } from "../../components/ProductLink";
import { ProductClient, type ProductCogsDTO, type ProductDTO, type ProductPackageDTO } from "../../api/product";
import { isoDate } from "../../api/reporting";

interface TabProps {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
}

/**
 * A bundle's cartons, which are its items' cartons.
 *
 * A bundle has no box of its own: it ships as the boxes of the things inside
 * it, and its SIGM part row carries no dimensions precisely because of that.
 * The tab used to answer "No packages defined", which is true of the record and
 * useless to anyone asking how the thing ships.
 *
 * Read-only throughout. These cartons belong to the items, and the place to
 * correct one is the item, not here, where an edit would silently disagree with
 * the product it came from.
 *
 * The lines carry ids and quantities only, so the items are fetched. That is
 * the same batched call the Items tab makes, and React Query dedupes it.
 */
function BundlePackages({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const products = useMemo(() => new ProductClient(http), [http]);

  const lines = draft.group_lines ?? [];
  const itemIds = lines
    .map((line) => line.product_id)
    .filter((id): id is number => typeof id === "number");

  const { data: items, isLoading } = useQuery({
    queryKey: ["product", "bundle-items", draft.id, itemIds.join(",")],
    queryFn: () => products.findByIds(itemIds),
    enabled: itemIds.length > 0,
  });

  const rows = useMemo(() => {
    if (!items) return [];
    return lines.flatMap((line) => {
      const item = items.find((candidate) => candidate.id === line.product_id);
      if (!item) return [];
      const quantity = line.quantity ?? 1;
      return (item.packages ?? []).map((pkg) => ({
        id: item.id,
        sku: item.metadata?.sku ?? "",
        quantity,
        pkg,
      }));
    });
  }, [items, lines]);

  // A bundle of two items where one ships twice is three boxes, not two, so the
  // quantity has to be in the count for it to mean anything to shipping.
  const totals = useMemo(
    () =>
      rows.reduce(
        (accumulator, row) => ({
          boxes: accumulator.boxes + row.quantity,
          weight: accumulator.weight + (row.pkg.weight_in_lbs ?? 0) * row.quantity,
        }),
        { boxes: 0, weight: 0 },
      ),
    [rows],
  );

  if (itemIds.length === 0) {
    return (
      <p className="text-[13px] text-ink-faint">
        {t("product.measure.packages.no-items", "This bundle has no items yet.")}
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-[13px] text-ink-faint">{t("common.loading", "Loading...")}</p>;
  }

  if (rows.length === 0) {
    return (
      <p className="text-[13px] text-ink-faint">
        {t("product.measure.packages.items-none", "None of this bundle's items has a package.")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] uppercase tracking-wider text-ink-faint">
              <th className="pb-2 pr-3 font-medium">{t("product.measure.item-column", "Item")}</th>
              <th className="pb-2 pr-3 font-medium">
                {t("product.measure.package-sku", "Package SKU")}
              </th>
              <th className="pb-2 pr-3 text-right font-medium">
                {t("product.measure.width", "Width")}
              </th>
              <th className="pb-2 pr-3 text-right font-medium">
                {t("product.measure.depth", "Depth")}
              </th>
              <th className="pb-2 pr-3 text-right font-medium">
                {t("product.measure.height", "Height")}
              </th>
              <th className="pb-2 text-right font-medium">
                {t("product.measure.weight", "Weight")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.id}:${index}`} className="border-t border-line-soft">
                <td className="py-1.5 pr-3">
                  <span className="flex items-center gap-1.5">
                    <ProductLink id={row.id}>{row.sku}</ProductLink>
                    {row.quantity > 1 && (
                      <span className="text-[12px] text-ink-faint">x{row.quantity}</span>
                    )}
                  </span>
                </td>
                <td className="py-1.5 pr-3 text-ink-muted">{row.pkg.package_sku ?? ""}</td>
                <td className="tabular py-1.5 pr-3 text-right">{row.pkg.width_in_inches ?? ""}</td>
                <td className="tabular py-1.5 pr-3 text-right">{row.pkg.depth_in_inches ?? ""}</td>
                <td className="tabular py-1.5 pr-3 text-right">{row.pkg.height_in_inches ?? ""}</td>
                <td className="tabular py-1.5 text-right">{row.pkg.weight_in_lbs ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[12px] text-ink-muted">
        {t("product.measure.packages.totals", "{{boxes}} box(es), {{weight}} lb in total", {
          boxes: totals.boxes,
          weight: Math.round(totals.weight * 100) / 100,
        })}
      </p>
    </div>
  );
}

/** Physical dimensions, used by shipping and by every retailer feed. */
export function MeasurementsTab({ draft, onChange, readOnly }: TabProps) {
  const { t } = useTranslation();
  const measurements = draft.measurements ?? {};

  const set = (key: keyof typeof measurements, value: string) =>
    onChange({
      ...draft,
      measurements: { ...measurements, [key]: value === "" ? null : Number(value) },
    });

  const fields: Array<{ key: keyof typeof measurements; label: string; unit: string }> = [
    { key: "width_in_inches", label: t("product.measure.width", "Width"), unit: "in" },
    { key: "height_in_inches", label: t("product.measure.height", "Height"), unit: "in" },
    { key: "depth_in_inches", label: t("product.measure.depth", "Depth"), unit: "in" },
    { key: "weight_in_lbs", label: t("product.measure.weight", "Weight"), unit: "lb" },
  ];

  // Bundles aggregate their items' cartons; nothing to edit at the bundle level.
  const packagesLocked = readOnly || draft.type === "BUNDLE";
  const packages = draft.packages ?? [];

  const setPackage = (index: number, patch: Partial<ProductPackageDTO>) =>
    onChange({
      ...draft,
      packages: packages.map((pkg, position) => (position === index ? { ...pkg, ...patch } : pkg)),
    });

  const numeric = (value: string): number | null => {
    const parsed = Number(value);
    return value === "" || Number.isNaN(parsed) ? null : parsed;
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("product.measure.item", "Item")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {fields.map((field) => (
            <Field key={field.key} label={`${field.label} (${field.unit})`} htmlFor={field.key}>
              <Input
                id={field.key}
                type="number"
                step="0.01"
                value={measurements[field.key] ?? ""}
                readOnly={readOnly}
                onChange={(event) => set(field.key, event.target.value)}
              />
            </Field>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex items-center gap-3">
          <h3 className="text-[13px] font-semibold text-ink">
            {t("product.measure.packages", "Package measurements")}
          </h3>
          {draft.type === "BUNDLE" && (
            <span className="text-[12px] text-ink-faint">
              {t("product.measure.packages.bundle", "Aggregated from the bundle's items")}
            </span>
          )}
          {!packagesLocked && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              className="ml-auto"
              onClick={() =>
                onChange({
                  ...draft,
                  packages: [
                    ...packages,
                    // Seed from the product's own identity, as the old form did.
                    { package_sku: draft.metadata?.sku ?? "", package_upc: draft.metadata?.upc ?? "" },
                  ],
                })
              }
            >
              {t("product.measure.packages.add", "Add package")}
            </Button>
          )}
        </div>

        {draft.type === "BUNDLE" ? (
          <BundlePackages draft={draft} />
        ) : packages.length === 0 ? (
          <p className="text-[13px] text-ink-faint">
            {t("product.measure.packages.none", "No packages defined.")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className="grid items-end gap-3 rounded-md border border-line-soft p-3 sm:grid-cols-3 xl:grid-cols-7"
              >
                <p className="text-[12px] font-medium uppercase tracking-wider text-ink-faint xl:self-center">
                  {t("product.measure.package-n", "Package {{n}}", { n: index + 1 })}
                </p>
                {(
                  [
                    ["width_in_inches", t("product.measure.width", "Width")],
                    ["depth_in_inches", t("product.measure.depth", "Depth")],
                    ["height_in_inches", t("product.measure.height", "Height")],
                    ["weight_in_lbs", t("product.measure.weight", "Weight")],
                  ] as const
                ).map(([key, label]) => (
                  <Field key={key} label={label} htmlFor={`pkg-${index}-${key}`}>
                    <Input
                      id={`pkg-${index}-${key}`}
                      type="number"
                      step="0.01"
                      value={pkg[key] ?? ""}
                      readOnly={packagesLocked}
                      onChange={(event) => setPackage(index, { [key]: numeric(event.target.value) })}
                    />
                  </Field>
                ))}
                <Field label={t("product.measure.package-sku", "Package SKU")} htmlFor={`pkg-${index}-sku`}>
                  <Input
                    id={`pkg-${index}-sku`}
                    value={pkg.package_sku ?? ""}
                    readOnly={packagesLocked}
                    onChange={(event) => setPackage(index, { package_sku: event.target.value })}
                  />
                </Field>
                <div className="flex items-end gap-2">
                  <Field
                    label={t("product.measure.package-upc", "Package UPC")}
                    htmlFor={`pkg-${index}-upc`}
                    className="flex-1"
                  >
                    <Input
                      id={`pkg-${index}-upc`}
                      value={pkg.package_upc ?? ""}
                      readOnly={packagesLocked}
                      onChange={(event) => setPackage(index, { package_upc: event.target.value })}
                    />
                  </Field>
                  {!packagesLocked && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mb-1 size-7 p-0 hover:text-risk"
                      aria-label={t("button.delete", "Delete")}
                      onClick={() =>
                        onChange({
                          ...draft,
                          packages: packages.filter((_, position) => position !== index),
                        })
                      }
                      icon={<Trash2 className="size-3.5" />}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Cost history. The figures themselves come from the COGS computation and are
 * never hand-edited, but which run is *active* is a decision — the Vaadin grid
 * put an Activate button on every inactive row, and so does this. It lands in
 * the draft, so it commits with Save like every other tab.
 */
export function CogsTab({ draft, onChange, readOnly }: TabProps) {
  const { t } = useTranslation();

  // A product has exactly one active cost, so activating one clears the rest.
  const activate = (id: number) =>
    onChange({
      ...draft,
      cogs: (draft.cogs ?? []).map((entry) => ({
        ...entry,
        is_active: entry.id === id,
        date_activated:
          entry.id === id ? isoDate(new Date()) : entry.date_activated,
      })),
    });

  const cols = useMemo(() => {
    const c = defineColumns<ProductCogsDTO>();
    const amount = (id: string, headerKey: string, pick: (row: ProductCogsDTO) => number | null | undefined, priority = 2) =>
      c.custom(id, (row) => pick(row) ?? 0, (row) => <span className="tabular">{money(pick(row))}</span>, {
        headerKey,
        filter: "number",
        align: "right",
        priority,
      });
    return [
      c.custom(
        "period",
        (row) => row.date_from ?? "",
        (row) => (
          <span className="flex items-center gap-2">
            {row.date_from ?? ""} → {row.date_to ?? t("product.cogs.current", "current")}
            {row.is_active && (
              <Badge variant="success" size="sm">
                {t("product.cogs.active", "Active")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "product.cogs.period", priority: 1 },
      ),
      c.number("produced", (row) => row.produced ?? 0, { headerKey: "column.produced", priority: 2 }),
      amount("material", "product.cogs.material", (row) => row.material_cogs),
      amount("prod-labor", "product.cogs.prod-labor", (row) => row.production_labor_cogs),
      amount("prod-overhead", "product.cogs.prod-overhead", (row) => row.production_overhead_cogs, 3),
      amount("asm-labor", "product.cogs.asm-labor", (row) => row.assembly_labor_cogs, 3),
      amount("asm-overhead", "product.cogs.asm-overhead", (row) => row.assembly_overhead_cogs, 3),
      c.custom(
        "cogs",
        (row) => row.cogs ?? 0,
        (row) => <span className="tabular font-semibold text-ink">{money(row.cogs)}</span>,
        { headerKey: "column.cogs", filter: "number", align: "right", priority: 1 },
      ),
    ];
  }, [t]);

  return (
    <DataTable
      data={draft.cogs ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      initialSorting={[{ id: "period", desc: true }]}
      emptyMessage={t("product.cogs.none", "No cost breakdown computed yet.")}
      rowActionsHeader={t("column.option", "Options")}
      rowActions={(row) =>
        !row.is_active && !readOnly ? (
          <Button size="sm" variant="success" onClick={() => activate(row.id)}>
            {t("button.activate", "Activate")}
          </Button>
        ) : null
      }
    />
  );
}

/**
 * Free-text notes. Added and removed in the draft like every other tab, so they
 * commit with Save rather than writing immediately.
 */
export function NotesTab({ draft, onChange, readOnly }: TabProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const notes = draft.notes ?? [];

  const add = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onChange({
      ...draft,
      // id 0 marks a row the server has not seen yet.
      notes: [{ id: 0, note: trimmed, created_at: new Date().toISOString() }, ...notes],
    });
    setText("");
  };

  return (
    <div className="flex flex-col gap-4">
      {!readOnly && (
        <Card className="p-4">
          <Field label={t("product.notes.add", "Add a note")} htmlFor="note">
            <textarea
              id="note"
              rows={3}
              value={text}
              onChange={(event) => setText(event.target.value)}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
            />
          </Field>
          <div className="mt-2 flex justify-end">
            <Button variant="primary" size="sm" disabled={!text.trim()} onClick={add}>
              {t("button.add", "Add")}
            </Button>
          </div>
        </Card>
      )}

      {notes.length === 0 ? (
        <TabNotPorted message={t("product.notes.none", "No notes on this product.")} />
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.map((note, index) => (
            <li key={note.id || `new-${index}`}>
              <Card className="flex items-start gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="whitespace-pre-wrap text-[13px] text-ink">{note.note}</p>
                  <p className="mt-1 text-[11px] text-ink-faint">
                    {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
                    {note.id === 0 && ` · ${t("product.notes.unsaved", "unsaved")}`}
                  </p>
                </div>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="size-7 shrink-0 p-0"
                    aria-label={t("button.delete", "Delete")}
                    onClick={() =>
                      onChange({ ...draft, notes: notes.filter((_, position) => position !== index) })
                    }
                    icon={<Trash2 className="size-3.5" />}
                  />
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function money(value: number | null | undefined): string {
  return value === null || value === undefined
    ? ""
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "CAD" }).format(value);
}
